import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", border: "#D6E4F0", white: "#FFFFFF", muted: "#6B85A3",
};

function getInitials(email: string): string {
  const local = email.split("@")[0] || "";
  if (local.includes(".")) {
    const parts = local.split(".");
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

interface AvatarDropdownProps {
  email: string;
  onOpenFiscalModal: () => void;
  onExportPDF?: () => void;
  showChevron?: boolean;
}

export default function AvatarDropdown({ email, onOpenFiscalModal, onExportPDF, showChevron }: AvatarDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initials = getInitials(email);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth?mode=login");
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 cursor-pointer transition-transform hover:scale-105"
        title="Mon compte"
        style={{ minWidth: 44, minHeight: 44 }}
      >
        <span
          className="rounded-full flex items-center justify-center text-[15px] font-bold text-white shrink-0"
          style={{ backgroundColor: C.teal, width: 44, height: 44 }}
        >
          {initials}
        </span>
        {showChevron && (
          <span className="text-xs hidden sm:inline" style={{ color: "#8BAAC8" }}>▾</span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile overlay */}
          <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setOpen(false)} />
          <div
            className="fixed right-2 left-2 bottom-2 z-50 sm:absolute sm:right-0 sm:left-auto sm:bottom-auto sm:top-12 sm:w-52 rounded-xl overflow-hidden"
            style={{
              backgroundColor: C.white,
              border: `1px solid ${C.border}`,
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              maxHeight: "calc(100vh - 80px)",
              overflowY: "auto",
            }}
          >
            <div className="px-3 py-2 border-b text-[11px] truncate" style={{ borderColor: C.border, color: C.muted }}>
              {email}
            </div>
            <button
              onClick={() => { setOpen(false); navigate("/profil"); }}
              className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-gray-50 transition-colors flex items-center gap-2"
              style={{ color: C.navy, minHeight: 40 }}
            >
              👤 Mon profil
            </button>
            <button
              onClick={() => { setOpen(false); onOpenFiscalModal(); }}
              className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-gray-50 transition-colors flex items-center gap-2"
              style={{ color: C.navy, minHeight: 40 }}
            >
              ⚙️ Statut fiscal
            </button>
            {onExportPDF && (
              <button
                onClick={() => { setOpen(false); onExportPDF(); }}
                className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-gray-50 transition-colors flex items-center gap-2"
                style={{ color: C.navy, minHeight: 40 }}
              >
                📄 Export PDF
              </button>
            )}
            <div style={{ borderTop: `1px solid ${C.border}` }}>
              <button
                onClick={handleSignOut}
                className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-red-50 transition-colors flex items-center gap-2"
                style={{ color: "#E63946", minHeight: 40 }}
              >
                🚪 Se déconnecter
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
